import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  GitBranch,
  Bot,
  Radar,
  MessageSquarePlus,
  ShieldCheck,
  ArrowRight,
  Server,
  LayoutDashboard,
  Database,
  Zap,
  Rocket,
  Terminal,
  Cpu,
  Code2,
  CheckCircle2
} from 'lucide-react';
import SEO from '../components/SEO';

export const Vector: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [employees, setEmployees] = useState('');
  const [phone, setPhone] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [nightmare, setNightmare] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const whatsappUrl = 'https://chat.whatsapp.com/KxmENLfpYJ1LpZY8mGXYiR?mode=ems_copy_t';
  const { t, i18n } = useTranslation();
  
  const capabilitiesData = t('vector.capabilities', { returnObjects: true }) as any[];
  const capabilities = [
    {
      icon: GitBranch,
      title: capabilitiesData[0]?.title,
      body: capabilitiesData[0]?.body
    },
    {
      icon: Bot,
      title: capabilitiesData[1]?.title,
      body: capabilitiesData[1]?.body
    },
    {
      icon: Radar,
      title: capabilitiesData[2]?.title,
      body: capabilitiesData[2]?.body
    }
  ];

  const heroHighlights = t('vector.heroHighlights', { returnObjects: true }) as any[];
  const uiBullets = t('vector.ui.bullets', { returnObjects: true }) as string[];
  
  const architectureData = t('vector.architecture.sections', { returnObjects: true }) as any[];
  const architecture = [
    {
      icon: Server,
      title: architectureData[0]?.title,
      items: architectureData[0]?.items
    },
    {
      icon: LayoutDashboard,
      title: architectureData[1]?.title,
      items: architectureData[1]?.items
    },
    {
      icon: Database,
      title: architectureData[2]?.title,
      items: architectureData[2]?.items
    }
  ];

  const roadmap = t('vector.roadmap.items', { returnObjects: true }) as any[] || [];
  
  const futureVision = {
    badge: t('vector.future.badge'),
    title: t('vector.future.title'),
    cta: t('vector.future.cta'),
    button: t('vector.future.button'),
    bullets: (t('vector.future.bullets', { returnObjects: true }) as string[]) || []
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !name) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/vector-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, company, role, employees, phone, vatNumber, nightmare, language: i18n.language })
      });
      if (!res.ok) throw new Error('Signup failed');
      setSuccess(true);
    } catch (_) {
      alert(t('vector.form.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <SEO
        title="Vector – Intelligent Workflow Automation"
        description={t('vector.hero.subtitle') as string}
        canonical="https://victorlazari.com/vector"
        type="website"
      />
      <div className="relative overflow-hidden bg-slate-950 text-slate-200 font-sans selection:bg-cyan-500/30 min-h-screen">
        {/* Tech Background */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 pointer-events-none" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.8)_100%)] pointer-events-none" />
        
        {/* Deep Background Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-cyan-900/10 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />
        
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mx-auto max-w-6xl"
          >
            {/* HERO CARD */}
            <div className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-900/80 backdrop-blur-xl px-8 py-12 md:px-16 md:py-20 shadow-2xl">
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-cyan-900/10 to-transparent pointer-events-none" />
              
              <div className="relative grid gap-16 lg:grid-cols-[1.2fr_0.8fr] items-center">
                <div>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-16 h-16 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-center shadow-lg group relative overflow-hidden">
                       <div className="absolute inset-0 bg-cyan-500/10 animate-pulse" />
                       <Terminal className="text-cyan-500 w-8 h-8 relative z-10" />
                    </div>
                    <div>
                      <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-cyan-950/50 border border-cyan-500/30 text-cyan-400 text-[10px] font-mono uppercase tracking-wider mb-1">
                        <Zap size={10} /> {t('vector.hero.badge')}
                      </div>
                      <p className="text-xs text-slate-500 font-mono">
                        {t('vector.hero.systemStatus')} <span className="text-emerald-500 animate-pulse">{t('vector.hero.status')}</span>
                      </p>
                    </div>
                  </div>
                  
                  <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-white leading-none mb-6">
                    {t('vector.hero.title')}<br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">{t('vector.hero.titleHighlight')}</span>
                  </h1>
                  
                  <p className="text-lg text-slate-400 leading-relaxed mb-10 max-w-xl">
                    {t('vector.hero.subtitle')}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <a
                      href="#vector-form"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-cyan-600 text-white font-bold shadow-lg shadow-cyan-900/20 hover:bg-cyan-500 hover:scale-[1.02] transition-all w-full sm:w-auto"
                    >
                      {t('vector.hero.cta')}
                      <ArrowRight className="w-5 h-5" />
                    </a>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl border border-slate-700 bg-slate-800/50 text-slate-300 font-medium hover:bg-slate-800 hover:text-white transition-all w-full sm:w-auto group"
                    >
                      <MessageSquarePlus className="w-5 h-5 text-green-500 group-hover:scale-110 transition-transform" />
                      {t('vector.hero.chat')}
                    </a>
                  </div>
                </div>

                <div className="space-y-4">
                  {heroHighlights.map(({ chip, title, body }, idx) => (
                    <motion.div 
                      key={idx} 
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + (idx * 0.1) }}
                      className="group relative rounded-xl border border-slate-800 bg-slate-950/50 p-5 hover:border-cyan-500/30 transition-all hover:-translate-x-1"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 group-hover:text-cyan-400 transition-colors">{chip}</p>
                        <Bot className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors" />
                      </div>
                      <h3 className="text-base font-bold text-white mb-1">{title}</h3>
                      <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Capabilities Grid */}
          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="mt-24"
          >
            <div className="flex items-center gap-4 mb-12">
               <div className="h-px flex-1 bg-slate-800" />
               <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">{t('vector.coreModules')}</span>
               <div className="h-px flex-1 bg-slate-800" />
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {capabilities.map((cap, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -6 }}
                  className="rounded-2xl border border-slate-800 bg-slate-900/30 p-8 hover:bg-slate-900 hover:border-cyan-500/20 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center text-slate-400 mb-6 group-hover:text-cyan-400 group-hover:border-cyan-500/30 transition-colors">
                     <cap.icon className="w-6 h-6" />
                  </div>
                  <p className="text-xl font-bold text-white leading-tight mb-3 group-hover:text-cyan-400 transition-colors">{cap.title}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{cap.body}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* UI Showcase */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.1 }}
            className="mt-32 grid lg:grid-cols-[0.85fr_1.15fr] gap-16 items-center"
          >
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-900 text-xs font-mono uppercase tracking-widest text-slate-300">
                <LayoutDashboard size={12} /> {t('vector.ui.badge')}
              </div>
              <h2 className="text-4xl font-bold text-white leading-tight">
                {t('vector.ui.title')}
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed">
                {t('vector.ui.description')}
              </p>

              <ul className="space-y-4">
                {uiBullets.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-4 text-sm text-slate-300 leading-relaxed group">
                    <div className="mt-1 min-w-[20px]">
                       <CheckCircle2 className="w-5 h-5 text-slate-600 group-hover:text-cyan-500 transition-colors" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
              <div className="relative rounded-xl border border-slate-700 bg-slate-900 overflow-hidden shadow-2xl">
                 {/* Fake UI Header */}
                 <div className="h-8 bg-slate-950 border-b border-slate-800 flex items-center px-4 gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/20" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/20" />
                    <div className="w-3 h-3 rounded-full bg-green-500/20" />
                 </div>
                 <img
                    src="/images/vector-admin%20pannel.webp"
                    alt="Dashboard"
                    className="w-full h-auto object-cover opacity-90 hover:opacity-100 transition-opacity"
                 />
              </div>
            </div>
          </motion.section>

          {/* Technical Deep Dive - The "Blueprints" */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ delay: 0.1 }}
            className="mt-32"
          >
            <div className="text-center mb-16">
               <h3 className="text-3xl font-bold text-white mb-4">{t('vector.architecture.title')}</h3>
               <p className="text-slate-400">{t('vector.architecture.subtitle')}</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {architecture.map(({ icon: Icon, title, items }, idx) => (
                <div key={idx} className="rounded-2xl border border-slate-800 bg-slate-950/50 p-8 hover:border-slate-700 transition-colors">
                  <div className="flex items-center gap-4 mb-6">
                     <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 text-cyan-500">
                        <Icon className="w-6 h-6" />
                     </div>
                     <h3 className="text-lg font-bold text-white">{title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {items?.map((line: string, idx2: number) => (
                      <li key={idx2} className="flex items-start gap-3 text-sm text-slate-400 font-mono">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-cyan-500 flex-shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Roadmap - The Timeline */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ delay: 0.1 }}
            className="mt-32"
          >
            <h3 className="text-3xl font-bold text-white text-center mb-16">{t('vector.roadmap.title')}</h3>
            <div className="relative">
               <div className="absolute top-1/2 left-0 w-full h-px bg-slate-800 -translate-y-1/2 hidden md:block" />
               <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  {roadmap.map((item, index) => (
                     <div key={index} className="relative bg-slate-900 border border-slate-800 p-6 rounded-xl z-10 hover:border-cyan-500/30 transition-colors group">
                        <div className="text-xs font-mono text-slate-500 mb-2 group-hover:text-cyan-400 transition-colors">PHASE {item.phase}</div>
                        <h4 className="text-white font-bold text-sm mb-4">{item.title}</h4>
                        <div className={`inline-flex items-center px-2 py-1 rounded border text-[10px] font-mono uppercase tracking-wider ${
                           item.status === 'Completo' || item.status === 'Complete' 
                              ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-400' 
                              : 'bg-slate-800 border-slate-700 text-slate-400'
                        }`}>
                           {item.status}
                        </div>
                     </div>
                  ))}
               </div>
            </div>
          </motion.section>

          {/* Future Vision */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ delay: 0.1 }}
            className="mt-32 rounded-[2rem] border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-10 md:p-16 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px] pointer-events-none" />
            
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-12 relative z-10">
              <div className="lg:w-1/2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-purple-500/30 bg-purple-900/20 text-purple-400 text-xs font-mono uppercase tracking-widest mb-6">
                  <Rocket size={12} /> {futureVision.badge}
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-6">{futureVision.title}</h3>
                <ul className="space-y-4">
                  {futureVision.bullets.map((line: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-4 text-slate-300">
                      <div className="mt-1 p-1 bg-purple-500/20 rounded text-purple-400">
                         <Cpu size={14} />
                      </div>
                      <span className="text-sm leading-relaxed">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:w-1/3 bg-slate-950/50 border border-slate-800 rounded-2xl p-8 backdrop-blur-sm flex flex-col justify-between h-full">
                <p className="text-sm text-slate-400 leading-relaxed mb-8 font-mono border-l-2 border-purple-500 pl-4">
                  "{futureVision.cta}"
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white text-black font-bold hover:bg-slate-200 transition-colors"
                >
                  {futureVision.button}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.section>

          {/* Sign Up Form - The "Terminal" Style */}
          <section id="vector-form" className="mt-32">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              className="relative p-1 rounded-[2rem] bg-gradient-to-br from-slate-800 to-slate-900"
            >
              <div className="bg-slate-950 rounded-[1.9rem] p-8 md:p-12">
                 {!success ? (
                    <>
                      <div className="text-center mb-12">
                         <h2 className="text-3xl font-bold text-white mb-4">{t('vector.form.title')}</h2>
                         <p className="text-slate-400">{t('vector.form.subtitle')}</p>
                      </div>
                      
                      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                        {/* Styled Inputs */}
                        {[
                           { label: t('vector.form.email'), value: email, set: setEmail, type: 'email' },
                           { label: t('vector.form.name'), value: name, set: setName, type: 'text' },
                           { label: t('vector.form.company'), value: company, set: setCompany, type: 'text' },
                           { label: t('vector.form.role'), value: role, set: setRole, type: 'text' },
                           { label: t('vector.form.employees', 'Employees'), value: employees, set: setEmployees, type: 'text' },
                           { label: t('vector.form.phone', 'Phone'), value: phone, set: setPhone, type: 'tel' },
                           { label: t('vector.form.vat', 'VAT Number'), value: vatNumber, set: setVatNumber, type: 'text' },
                        ].map((field, i) => (
                           <div key={i} className="space-y-2">
                              <label className="text-xs font-mono text-slate-500 uppercase tracking-wider">{field.label}</label>
                              <input 
                                 type={field.type} 
                                 value={field.value}
                                 onChange={(e) => field.set(e.target.value)}
                                 className="w-full bg-slate-900 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                              />
                           </div>
                        ))}
                        
                        {/* Full Width Inputs */}
                        <div className="md:col-span-2 space-y-2">
                           <label className="text-xs font-mono text-slate-500 uppercase tracking-wider">{t('vector.form.nightmare')}</label>
                           <textarea 
                              value={nightmare}
                              onChange={(e) => setNightmare(e.target.value)}
                              className="w-full bg-slate-900 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors min-h-[100px]"
                           />
                        </div>

                        <div className="md:col-span-2 pt-4">
                           <button 
                              type="submit" 
                              disabled={submitting}
                              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold py-4 rounded-xl hover:shadow-lg hover:shadow-cyan-500/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                           >
                              {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <Code2 size={20} />}
                              {submitting ? t('vector.form.processing') : t('vector.form.button')}
                           </button>
                        </div>
                      </form>
                    </>
                 ) : (
                    <div className="text-center py-12">
                       <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                          <ShieldCheck className="w-10 h-10 text-emerald-500" />
                       </div>
                       <h3 className="text-2xl font-bold text-white mb-4">{t('vector.form.success.title')}</h3>
                       <p className="text-slate-400 max-w-md mx-auto mb-8">
                          {t('vector.form.success.message')}
                       </p>
                       <button className="px-6 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white font-mono text-sm hover:bg-slate-800 transition-colors">
                          {t('vector.form.success.button')}
                       </button>
                    </div>
                 )}
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Vector;